import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
// import { useUpload } from "@/hooks/use-upload";
import { ContentAffirmationService } from "@/lib/api/affirmation.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  AffirmationLanguageVariantDraft,
  Category,
  Coach,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AffirmationVariantFields } from "./affirmation-variant-fields";

const affirmationConfig = CONTENT_TYPE_CONFIG.affirmation;

const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

export default function AffirmationCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  // Background image upload disabled — kept for future reference.
  // const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [categoryId, setCategoryId] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [variants, setVariants] = useState<AffirmationLanguageVariantDraft[]>([
    {
      languageCode: "en",
      isDefault: true,
      quoteText: "",
      authorName: null,
      coachId: null,
      featuredImageUrl: null,
    },
  ]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId]);

  const createMutation = useMutation({
    mutationFn: async (submitForApproval: boolean) => {
      if (selectedTagOptionIds.length === 0) {
        throw new Error("At least one mood tag is required");
      }
      const defaultVariant = variants.find((v) => v.isDefault) || variants[0];
      if (!defaultVariant?.quoteText?.trim()) {
        throw new Error("Quote text is required");
      }

      const coach = authorCoachId
        ? coaches.find((c) => c.id === authorCoachId)
        : null;
      const resolvedAuthorName =
        coach?.name ??
        (isCoachOnly && authorCoachId === user?.id
          ? (user?.name ?? null)
          : null);

      const payload = {
        affirmation: {
          tagOptionIds: selectedTagOptionIds,
          categoryId: categoryId || undefined,
          authorCoachId: authorCoachId || undefined,
          primaryLanguage: defaultVariant.languageCode,
          internalNotes: internalNotes || null,
        },
        variants: variants.map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          quoteText: v.quoteText,
          authorName: resolvedAuthorName,
          coachId: authorCoachId ?? null,
          featuredImageUrl: v.featuredImageUrl,
          translationStatus: v.isDefault ? "completed" : "pending",
        })),
      };

      const data = await ContentAffirmationService.create(payload);

      if (submitForApproval) {
        await ContentAffirmationService.submitForReview(data.id);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-affirmations"] });
      toast({ title: "Affirmation created" });
      setLocation("/studio/affirmations");
    },
    onError: (err: Error) => {
      toast({
        title: err.message || "Failed to create affirmation",
        variant: "destructive",
      });
    },
  });

  // Background image upload disabled — kept for future reference.
  // const handleFileUpload = async (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   field: keyof AffirmationLanguageVariantDraft,
  // ) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   const result = await uploadFile(file);
  //   if (result) {
  //     const updated = [...variants];
  //     updated[activeVariantIndex] = {
  //       ...updated[activeVariantIndex],
  //       [field]: result.objectPath,
  //     };
  //     setVariants(updated);
  //   }
  // };

  const updateVariant = (
    field: keyof AffirmationLanguageVariantDraft,
    value: any,
  ) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([
      ...variants,
      {
        languageCode: langCode,
        isDefault: false,
        quoteText: "",
        authorName: null,
        coachId: null,
        featuredImageUrl: null,
      },
    ]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (id: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  return (
    <BuilderShell
      contentTypeConfig={affirmationConfig}
      mode="create"
      onSave={() => createMutation.mutate(false)}
      onSubmit={() =>
        categoryId
          ? createMutation.mutate(true)
          : toast({
              title: "Select a category before submitting for approval.",
              variant: "destructive",
            })
      }
      isSaving={createMutation.isPending}
      status="draft"
    >
      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <BuilderSection
        title="Author"
        stepNumber={2}
        description="Select the coach attributed as the author"
        defaultOpen={true}
      >
        <div className="space-y-2">
          <Label>Author</Label>
          {isCoachOnly ? (
            <Input
              value={user?.name ?? ""}
              disabled
              data-testid="select-author-coach"
            />
          ) : (
            <Select
              value={authorCoachId || "none"}
              onValueChange={(val) =>
                setAuthorCoachId(val === "none" ? null : val)
              }
            >
              <SelectTrigger data-testid="select-author-coach">
                <SelectValue placeholder="Select author" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No author</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </BuilderSection>

      <LanguageVariantsSection
        stepNumber={3}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
      >
        {activeVariant && (
          <AffirmationVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            // onFileUpload={handleFileUpload}
            // isUploading={isUploading}
          />
        )}
      </LanguageVariantsSection>

      <RefinementSection
        stepNumber={4}
        internalNotes={internalNotes}
        onInternalNotesChange={setInternalNotes}
      />
    </BuilderShell>
  );
}
